import { BackButton } from "@/components/BackButton";
import { Logo } from "@/components/Logo";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Play, Share2, Star, GraduationCap, Sparkles, Tag } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PlayAlongNote } from "@/components/PlayAlongPromo";
import { OneToOne } from "@/components/OneToOne";
import { FAQ } from "@/components/FAQ";
import { PianoVideos } from "@/components/PianoVideos";
import { Button } from "@/components/ui/button";
import { getInstrument, INSTRUMENT_ART } from "@/data/curriculum";
import { api } from "@/lib/api";
import { GospelCard } from "@/components/GospelCourse";
import { Breadcrumbs } from "@/components/CourseNav";
import { CompareCourses } from "@/components/CompareCourses";

const COURSE_ICON = { foundations: GraduationCap, mastery: Sparkles };

const SAMPLE_QUOTE = {
  quote: "The lessons finally made sheet music click for me — I can read and play what I hear.",
  name: "A JT Hub learner",
  role: "Beginner → confident reader",
  rating: 5,
};

export default function InstrumentPage() {
  const { instrumentId } = useParams();
  const instrument = getInstrument(instrumentId);
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    if (!instrument) return;
    const prevTitle = document.title;
    document.title = `${instrument.label} — Learn to read music | The JT Hub`;
    const setMeta = (key, val, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    const desc = `${instrument.label}: ${instrument.tagline}. Two complete 10-lesson courses with interactive notation, quizzes and 1:1 sessions at The JT Hub.`;
    setMeta("description", desc);
    setMeta("og:title", `${instrument.label} — The JT Hub`, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:image", INSTRUMENT_ART[instrument.id], "property");
    return () => {
      document.title = prevTitle;
    };
  }, [instrument]);

  useEffect(() => {
    api
      .get("/testimonials")
      .then(({ data }) => {
        const list = data.testimonials || [];
        setTestimonial(list.length ? list[0] : SAMPLE_QUOTE);
      })
      .catch(() => setTestimonial(SAMPLE_QUOTE));
  }, []);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: `The JT Hub — ${instrument?.label}`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (e) {}
  };

  if (!instrument) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="font-serif text-3xl">Instrument not found</h1>
          <Link to="/courses" className="mt-6 inline-block">
            <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Browse courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const t = testimonial || SAMPLE_QUOTE;

  return (
    <div className="min-h-screen text-foreground" data-testid={`instrument-page-${instrument.id}`}>
      <Navbar />

      <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-8">
        <div className="mb-4"><BackButton testId="instrument-back-button" fallback="/courses" /></div>
        <Breadcrumbs hideBack items={[{ label: "All courses", to: "/courses" }, { label: instrument.label }]} />
      </div>
      {/* Hero */}
      <section className="relative mt-4 overflow-hidden">
        {instrument.id === "pianists" ? (
          <div className="relative h-72 w-full bg-card sm:h-96">
            <img src={INSTRUMENT_ART[instrument.id]} alt="Jason Thompson playing a grand piano" className="absolute right-0 top-0 h-full w-3/5 object-cover object-[center_47%] sm:w-1/2 lg:w-2/5" data-testid="instrument-hero-image" />
          </div>
        ) : (
          <img src={INSTRUMENT_ART[instrument.id]} alt={instrument.label} className="h-72 w-full object-cover object-[center_32%] sm:h-96" data-testid="instrument-hero-image" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
        <div className="absolute inset-0 mx-auto flex max-w-6xl flex-col justify-end px-4 pb-8 sm:px-8">
          <div className="mb-3" data-testid="instrument-logo"><Logo height={56} /></div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Learn to read music</p>
          <h1 className="mt-2 font-serif text-5xl tracking-tight sm:text-6xl">{instrument.label}</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">{instrument.tagline}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to={`/courses/${instrument.id}/${instrument.courses[0].id}`}>
              <Button className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground hover:bg-primary/90" data-testid="instrument-start">
                Start this instrument <ArrowRight size={17} className="ml-2" />
              </Button>
            </Link>
            <button
              onClick={share}
              data-testid="instrument-share"
              className="flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              <Share2 size={15} /> Share
            </button>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8 lg:py-16">
        {/* Courses */}
        <h2 className="font-serif text-2xl">Two courses, ten lessons each</h2>
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {instrument.courses.map((course, i) => {
            const Icon = COURSE_ICON[course.id] || GraduationCap;
            return (
              <motion.div
                key={course.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.08 }}
                className="flex flex-col rounded-3xl border border-border bg-card p-8"
                data-testid={`instrument-course-${course.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-primary">
                    <Icon size={22} />
                  </span>
                  <span className="rounded-full bg-primary/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-primary">
                    {course.tagline}
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-2xl leading-tight">{course.title}</h3>
                <p className="mt-3 flex-1 text-sm text-muted-foreground">{course.blurb}</p>
                <PlayAlongNote testId={`instrument-playalong-note-${course.id}`} />

                <div className="mt-6 space-y-2">
                  {course.lessons.slice(0, 3).map((l, idx) => (
                    <div key={l.id} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-mono text-xs text-primary">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="flex-1 truncate">{l.title}</span>
                    </div>
                  ))}
                  <p className="pl-6 text-xs text-muted-foreground">+ {course.lessons.length - 3} more lessons</p>
                </div>

                <div className="mt-6 flex items-center gap-3">
                  <Link to={`/courses/${instrument.id}/${course.id}`} className="flex-1" data-testid={`instrument-open-${course.id}`}>
                    <Button className="w-full rounded-full bg-primary py-6 text-base text-primary-foreground hover:bg-primary/90">
                      <Play size={16} className="mr-2" /> Start course
                    </Button>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
        <GospelCard instrument={instrument} />
        <CompareCourses instrument={instrument} />

        {/* Testimonial */}
        <div className="mt-14 rounded-3xl border border-border bg-secondary p-8 sm:p-12" data-testid="instrument-testimonial">
          <div className="flex gap-1 text-primary">
            {Array.from({ length: t.rating || 5 }).map((_, i) => (
              <Star key={i} size={16} fill="currentColor" />
            ))}
          </div>
          <p className="mt-4 max-w-3xl font-serif text-2xl leading-snug sm:text-3xl">"{t.quote}"</p>
          <p className="mt-4 text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{t.name}</span>
            {t.role ? ` · ${t.role}` : ""}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-border bg-card p-8">
          <div>
            <h3 className="font-serif text-2xl">Ready to read {instrument.label.toLowerCase()} music?</h3>
            <p className="mt-1 text-sm text-muted-foreground">Unlock the full path whenever you're ready.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing">
              <Button className="rounded-full bg-primary px-6 py-6 text-base text-primary-foreground hover:bg-primary/90" data-testid="instrument-pricing">
                <Tag size={16} className="mr-2" /> See pricing
              </Button>
            </Link>
            <Link to="/trainer">
              <Button variant="outline" className="rounded-full border-border px-6 py-6 text-base hover:bg-secondary">
                Open the Trainer
              </Button>
            </Link>
          </div>
        </div>

        <PianoVideos limit={3} compact instrumentId={instrument.id} />
        <div className="mt-14">
          <OneToOne />
        </div>
        <div className="mt-16 pb-4">
          <FAQ />
        </div>
      </div>
    </div>
  );
}
